/**
 * Sync record plumbing: local storage keys, id generation, record stamping,
 * and the mapping between the app's in-memory shapes and PocketBase rows.
 *
 * Split out of sync.ts so that file stays focused on the merge/push cycle.
 */
import {
  localStamp,
  type Exercise,
  type LocalStamp,
  type MetricValues,
  type Store,
  type Tombstone,
  type Workout,
  type WorkoutRecord,
} from '../types';

export const LAST_PULL_KEY = 'sync_lastPulledAt';
export const LAST_PUSH_KEY = 'sync_lastPushedAt';
export const ID_MAP_KEY = 'sync_idMap';
export const TOMBSTONE_KEY = 'sync_tombstones';
export const SYNCED_USER_KEY = 'sync_userId';

/** Maps a client-generated id to the PocketBase record id it was saved as. */
export type IdMap = Record<string, string | undefined>;

// --- helpers ----------------------------------------------------------

export function nowStamp(): LocalStamp {
  return localStamp(new Date().toISOString());
}

export function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.trunc(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.warn(`[sync] could not write ${key}`, err);
  }
}

export const idMap = (): IdMap => readJSON<IdMap>(ID_MAP_KEY, {});
export const saveIdMap = (map: IdMap): void => writeJSON(ID_MAP_KEY, map);
export const tombstones = (): Tombstone[] => readJSON<Tombstone[]>(TOMBSTONE_KEY, []);
export const saveTombstones = (list: Tombstone[]): void => writeJSON(TOMBSTONE_KEY, list);

// --- record stamping (called at each mutation) -------------------------

export function stampExercise(exercise: Exercise): Exercise {
  if (!exercise.id) exercise.id = newId();
  exercise.clientUpdated = nowStamp();
  return exercise;
}

export function stampWorkout(workout: Workout): Workout {
  if (!workout.id) workout.id = newId();
  workout.clientUpdated = nowStamp();
  return workout;
}

export function tombstoneWorkout(workout: Workout | undefined): void {
  if (!workout?.id) return;
  saveTombstones([
    ...tombstones(),
    { id: workout.id, type: 'workout', clientUpdated: nowStamp() },
  ]);
}

/**
 * Backfills ids on data created before sync existed, so a long-standing local
 * history can be adopted into an account without duplicating every record.
 */
export function ensureStamped(s: Store): boolean {
  let changed = false;

  for (const exercise of s.getExercises()) {
    if (!exercise.id) {
      exercise.id = newId();
      exercise.clientUpdated = nowStamp();
      changed = true;
    }
  }

  const workoutData = s.getWorkoutData();
  for (const list of Object.values(workoutData)) {
    for (const workout of list ?? []) {
      if (!workout.id) {
        workout.id = newId();
        workout.clientUpdated = nowStamp();
        changed = true;
      }
    }
  }

  return changed;
}

// --- local <-> remote shapes -------------------------------------------

export function workoutToRecord(workout: Workout, userId: string): Record<string, unknown> {
  const payload: MetricValues = {};
  if (workout.reps !== undefined) payload.reps = workout.reps;
  if (workout.weight !== undefined) payload.weight = workout.weight;
  if (workout.time !== undefined) payload.time = workout.time;

  return {
    user: userId,
    clientId: workout.id,
    exercise: workout.exercise,
    date: workout.date,
    payload,
    clientUpdated: workout.clientUpdated,
    deleted: false,
  };
}

export function recordToWorkout(record: WorkoutRecord): Workout {
  const workout: Workout = {
    id: record.clientId,
    date: record.date,
    exercise: record.exercise,
    clientUpdated: record.clientUpdated,
  };

  const payload = record.payload ?? {};
  if (payload.reps !== undefined && payload.reps !== null) workout.reps = payload.reps;
  if (payload.weight !== undefined && payload.weight !== null) workout.weight = payload.weight;
  if (payload.time !== undefined && payload.time !== null) workout.time = payload.time;

  return workout;
}

export function exerciseToRecord(exercise: Exercise, userId: string): Record<string, unknown> {
  return {
    user: userId,
    clientId: exercise.id,
    name: exercise.name,
    metrics: exercise.metrics,
    clientUpdated: exercise.clientUpdated,
    deleted: false,
  };
}
